import { useState, useEffect } from 'react'
import { analysisAPI } from '../../services/api'
import { AnnotatorStatistics } from '../../types'

interface AnnotatorStatsProps {
  projectId: string
}

export default function AnnotatorStats({ projectId }: AnnotatorStatsProps) {
  const [statistics, setStatistics] = useState<AnnotatorStatistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchAnnotatorStatistics()
  }, [projectId])

  const fetchAnnotatorStatistics = async () => {
    try {
      setLoading(true)
      const response = await analysisAPI.getAnnotatorStatistics(projectId)
      setStatistics(response.data)
    } catch (error) {
      console.error('Error fetching annotator statistics:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!statistics || Object.keys(statistics).length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No annotator statistics available</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Annotator Performance</h3>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Annotator
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Images Annotated
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Annotations
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Visible Keypoints
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Visibility Rate
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Object.entries(statistics).map(([userId, stats]) => (
              <tr key={userId}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{stats.name}</div>
                    <div className="text-sm text-gray-500">{stats.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {stats.image_count}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {stats.annotation_count}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {stats.visible_keypoints} / {stats.total_keypoints}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${stats.keypoint_visibility_rate * 100}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-900">
                      {(stats.keypoint_visibility_rate * 100).toFixed(1)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Performance Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Performance Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <p className="text-sm text-gray-600">Total Annotators</p>
            <p className="text-xl font-semibold text-gray-900">{Object.keys(statistics).length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Average Images per Annotator</p>
            <p className="text-xl font-semibold text-gray-900">
              {(
                Object.values(statistics).reduce((sum, s) => sum + s.image_count, 0) /
                Object.keys(statistics).length
              ).toFixed(1)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Average Visibility Rate</p>
            <p className="text-xl font-semibold text-gray-900">
              {(
                (Object.values(statistics).reduce((sum, s) => sum + s.keypoint_visibility_rate, 0) /
                  Object.keys(statistics).length) *
                100
              ).toFixed(1)}
              %
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
